import { ReactElement, useState } from "react";
import { useContextSelector } from "use-context-selector";
import { useMutation } from "@apollo/client";
import { Label, Select, Text, Box, TextArea } from "@xometry/xometry_loft";

import {
  ProposalPaymentTermsInput,
  UpsertProposalPaymentTermsQuery,
} from "../../../schema/generated/schema";

import ProposalContext from "../contexts/proposalContext";
import Modal from "../Common/Modal";

const EXPIRATION_DAYS_OPTIONS = [1, 3, 5, 7, 14, 30, 45, 60, 90];
const DEFAULT_EXPIRATION_DAY = 30;

const SubmissionModal = ({
  proposalId,
  buyerInformation,
  modalOpen,
  toggleModal,
  onSubmit,
  isSeller,
}: {
  proposalId: string;
  modalOpen: boolean;
  toggleModal: (boolean) => void;
  buyerInformation: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSubmit?: Function;
  isSeller?: boolean;
}): ReactElement => {
  const currentPaymentTerms = useContextSelector(
    ProposalContext,
    context => context.data?.getProposal?.proposal?.paymentTerms
  );

  const [formData, setFormData] = useState<ProposalPaymentTermsInput>({
    notes: currentPaymentTerms?.notes || "",
    id: currentPaymentTerms?.id || "",
    expiresAfterDays: DEFAULT_EXPIRATION_DAY,
  });

  const [upsertProposalPaymentTerms] = useMutation(
    UpsertProposalPaymentTermsQuery
  );

  const saveData = (name: string, value: string | number) => {
    setFormData(currentValue => ({ ...currentValue, [name]: value }));
  };

  const handleSave = async () => {
    await upsertProposalPaymentTerms({
      variables: {
        proposalId,
        item: formData,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      await handleSave();
      onSubmit?.();
      toggleModal(false);
    } catch (err) {
      console.log("failed validation");
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      selector="#modal-mount"
      title={
        <Text kind="panel-title">
          Submit Quote
          {buyerInformation && !isSeller ? ` to ${buyerInformation}` : ""}
        </Text>
      }
      description={
        <Text>
          {isSeller
            ? `Once you submit your quote, the recipient will be able to place an order with it. If the customer does not order the quote within ${
                formData.expiresAfterDays
              } day${
                formData.expiresAfterDays !== 1 ? "s" : ""
              }, the quote will automatically be cancelled`
            : "Once you submit your quote, your customer will be able to place an order directly from it."}
        </Text>
      }
      submitActionText={`${isSeller ? "Send" : "Submit"} Quote`}
      discardActionText={isSeller ? "Dismiss" : "Cancel"}
      submitButtonType={isSeller ? "primary" : "primary-thomas"}
      handleSubmit={handleSubmit}
      handleCloseButton={() => {
        toggleModal(false);
      }}
      handleCancel={() => {
        toggleModal(false);
      }}
    >
      {isSeller && (
        <Box className="recipientField">
          <Label className="recipient--label">Recipient</Label>
          <Text>{buyerInformation}</Text>
        </Box>
      )}
      <Label className="notes--label">
        Additional {isSeller ? "Notes" : "Terms"}
      </Label>
      <TextArea
        className="notes--textarea"
        scale="large"
        name="notes"
        placeholder={`Enter additional ${isSeller ? "notes" : "terms"}…`}
        onBlur={({ target }) => saveData(target.name, target.value)}
        defaultValue={formData?.notes || ""}
      />
      <Box className="expiresField">
        <Label className="expiresAfterDays--label">Quote Valid For</Label>
        <Select
          name="expiresAfterDays"
          onChange={({ target }) =>
            saveData(target.name, parseInt(target.value, 10))
          }
          className="expiresAfterDays--select"
          scale="large"
          defaultValue={formData?.expiresAfterDays || DEFAULT_EXPIRATION_DAY}
        >
          {EXPIRATION_DAYS_OPTIONS.map(item => (
            <option key={item} value={item}>
              {item} Day{item !== 1 && "s"}
            </option>
          ))}
        </Select>
        {!isSeller && (
          <div className="expiresAfterDays--description">
            <Text kind="body-sm">
              If your customer doesn&apos;t order the quote after{" "}
              {formData.expiresAfterDays} day
              {formData.expiresAfterDays !== 1 && "s"} we automatically cancel
              your quote.
            </Text>
          </div>
        )}
      </Box>
    </Modal>
  );
};

SubmissionModal.defaultProps = {
  onSubmit: undefined,
  isSeller: false,
};

export default SubmissionModal;
